import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Chip,
  Alert,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Assignment as AssignmentIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { getLiveProblemsBySession, getUserSubmissions, ProblemSubmission, Problem } from '../../services/problemService';
import { getSessionById, getLiveProblemGroupsBySession, Session, ProblemGroup } from '../../services/sessionService';
import { createTextPreview } from '../../utils/textUtils';

interface ProblemsPageProps {
  showStats?: boolean;
}

// Using modern function declaration with TypeScript instead of React.FC
const ProblemsPage = ({ showStats = false }: ProblemsPageProps) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [loading, setLoading] = useState(true);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [submissions, setSubmissions] = useState<ProblemSubmission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<{[key: number]: Problem[]}>({});
  const [session, setSession] = useState<Session | null>(null);
  const [problemGroups, setProblemGroups] = useState<ProblemGroup[]>([]); 

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!sessionId) {
          setError('Invalid session ID');
          setLoading(false);
          return;
        }
        
        const sessionIdNum = parseInt(sessionId);
        
        // Fetch session, problem groups, problems, and submissions in parallel
        const [
          fetchedSession, 
          fetchedProblemGroups, 
          fetchedProblems, 
          userSubmissions
        ] = await Promise.all([
          getSessionById(sessionIdNum),
          getLiveProblemGroupsBySession(sessionIdNum),
          getLiveProblemsBySession(sessionIdNum),
          getUserSubmissions()
        ]);
        
        // Convert string IDs to numbers to ensure consistent type comparison
        const processedGroups = fetchedProblemGroups.map(group => ({
          ...group,
          id: Number(group.id),
          sessionId: Number(group.sessionId)
        }));
        
        console.log("Processed problem groups:", processedGroups);
        console.log("Groups with videos:", processedGroups.filter(g => g.hintsVideoUrl));
        
        setSession(fetchedSession);
        setProblemGroups(processedGroups);
        setProblems(fetchedProblems);
        setSubmissions(userSubmissions);

        // Group problems by problem group ID
        const groupedProblems: {[key: number]: Problem[]} = {};
        
        // Ensure all problem groups have entries, even if they have no problems
        processedGroups.forEach(group => {
          groupedProblems[group.id] = [];
        });
        
        // Add problems to their respective groups
        fetchedProblems.forEach(problem => {
          const groupId = Number(problem.groupId);
          if (!groupedProblems[groupId]) {
            groupedProblems[groupId] = [];
          }
          groupedProblems[groupId].push({
            ...problem,
            groupId: Number(problem.groupId), // Ensure consistent number type
            sessionId: Number(problem.sessionId) // Ensure consistent number type
          });
        });
        
        console.log("Grouped problems by group ID:", groupedProblems);
        setGroups(groupedProblems);
      } catch (err) {
        console.error('Error fetching problems:', err);
        setError('Failed to load problems. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [sessionId]);
  
  // Function to check if a problem has been solved
  const isProblemSolved = (problemId: number): boolean => {
    return submissions.some(sub => sub.problemId === problemId && sub.isCorrect);
  };
  
  // Function to get submission result text and color
  const getSubmissionResult = (problemId: number): { text: string; color: string; chip: React.ReactNode } => {
    const submission = submissions.find(sub => sub.problemId === problemId);
    
    if (!submission) {
      return { 
        text: 'Not attempted yet', 
        color: 'text.secondary',
        chip: <Chip label="Not Started" color="default" size="small" variant="outlined" />
      };
    }
    
    if (submission.isCorrect) {
      return { 
        text: 'Correct!', 
        color: 'success.main',
        chip: <Chip label="Correct" icon={<CheckIcon />} color="success" size="small" />
      };
    }
    
    return { 
      text: 'Incorrect', 
      color: 'error.main',
      chip: <Chip label="Incorrect" icon={<CloseIcon />} color="error" size="small" />
    };
  };
  
  // Function to check if a problem group has ended
  const isGroupEnded = (groupId: string | number): boolean => {
    const groupIdNum = Number(groupId);
    const group = problemGroups.find(g => Number(g.id) === groupIdNum);
    
    if (!group || !group.endDate) {
      return false;
    }
    
    const now = new Date();
    const endDate = new Date(group.endDate);
    const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    
    return nowDateOnly > endDateOnly;
  };
  
  // Function to get problem group title by ID
  const getGroupTitle = (groupIdStr: string | number): string => {
    // Ensure group ID is a number for comparison
    const groupId = Number(groupIdStr);
    
    // Find the matching group, ensuring IDs are compared as numbers
    const group = problemGroups.find(g => Number(g.id) === groupId);
    
    if (group) {
      return group.title;
    } else {
      console.warn(`Group with ID ${groupId} not found in problemGroups array of length ${problemGroups.length}`);
      return `Problem Group ${groupId}`;
    }
  };

  const getInitiallyExpandedGroupId = (): number | null => {
    const groupIds = Object.keys(groups);
    
    // If there's only one group, expand it by default
    if (groupIds.length === 1) {
      return parseInt(groupIds[0]);
    }
    
    // Otherwise, look for a partially completed group
    for (const groupIdStr of groupIds) {
      const groupId = parseInt(groupIdStr);
      const groupProblems = groups[groupId];
      const solved = groupProblems.filter(p => isProblemSolved(p.id)).length;
      if (solved > 0 && solved < groupProblems.length) {
        return groupId;
      }
    }
    
    return null;
  };

  const initiallyExpandedGroupId = getInitiallyExpandedGroupId();

  return (
    <MainLayout>
      <Container maxWidth="lg">
        <Box sx={{ pt: 3, pb: 6 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {showStats ? 'Session Results' : (session ? session.title : 'Session Problems')}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {showStats
              ? `View your results and statistics for ${session?.title || 'this session'}`
              : (session ? session.description : 'Work on math problems for this session and track your progress.')}
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 2 }}>
              {error}
            </Alert>
          ) : problems.length === 0 ? (
            <Alert severity="info" sx={{ my: 2 }}>
              No problems found for this session yet. Check back later!
            </Alert>
          ) : (
            <>
              <Box sx={{ my: 3 }}>
                {showStats ? (
                  // Detailed statistics view
                  <Grid container spacing={3}>
                    {/* Overview stats card */}
                    <Grid item xs={12}>
                      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Session Overview
                        </Typography>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                          <Grid item xs={12} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" color="primary.main">
                                {problems.length}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Total Problems
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" color="primary.main">
                                {submissions.filter(s => s.isCorrect).length}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Solved Problems
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" 
                                color={submissions.length > 0 ? 'success.main' : 'text.secondary'}
                              >
                                {submissions.length > 0
                                  ? Math.round((submissions.filter(s => s.isCorrect).length / submissions.length) * 100)
                                  : 0}%
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Accuracy Rate
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h4" color="primary.main">
                                {Math.round((submissions.filter(s => s.isCorrect).length / problems.length) * 100)}%
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Completion Rate
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    {/* Difficulty breakdown */}
                    <Grid item xs={12} md={6}>
                      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                          Performance by Difficulty
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          {[1, 2, 3, 4, 5].map(level => {
                            const levelProblems = problems.filter(p => p.difficultyLevel === level);
                            const solvedProblems = levelProblems.filter(p => isProblemSolved(p.id));
                            const levelLabel = level === 1 ? 'Very Easy' : 
                                             level === 2 ? 'Easy' :
                                             level === 3 ? 'Medium' :
                                             level === 4 ? 'Hard' : 'Very Hard';
                            return (
                              <Box key={level} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="body2">
                                    {levelLabel}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {solvedProblems.length} / {levelProblems.length}
                                  </Typography>
                                </Box>
                                <Box sx={{ width: '100%', bgcolor: 'grey.100', borderRadius: 1, height: 8 }}>
                                  <Box 
                                    sx={{ 
                                      bgcolor: level <= 2 ? 'success.main' : level <= 3 ? 'primary.main' : 'error.main',
                                      height: 8,
                                      borderRadius: 1,
                                      width: `${levelProblems.length > 0 ? (solvedProblems.length / levelProblems.length) * 100 : 0}%`
                                    }}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Group breakdown */}
                    <Grid item xs={12} md={6}>
                      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                          Problem Group Progress
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          {Object.keys(groups).map((groupIdStr) => {
                            const groupId = parseInt(groupIdStr);
                            const groupProblems = groups[groupId];
                            const solvedInGroup = groupProblems.filter(p => isProblemSolved(p.id)).length;
                            const totalInGroup = groupProblems.length;
                            const completionPercentage = Math.round((solvedInGroup / totalInGroup) * 100);
                            return (
                              <Box key={groupId} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">
                                      {getGroupTitle(groupId)}
                                    </Typography>
                                    {isGroupEnded(groupId) && (
                                      <Chip 
                                        label="Ended" 
                                        icon={<ScheduleIcon />}
                                        size="small" 
                                        color="default" 
                                        variant="outlined"
                                        sx={{ fontSize: '0.6rem', height: '20px' }}
                                      />
                                    )}
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    {solvedInGroup} / {totalInGroup}
                                  </Typography>
                                </Box>
                                <Box sx={{ width: '100%', bgcolor: 'grey.100', borderRadius: 1, height: 8 }}>
                                  <Box 
                                    sx={{ 
                                      bgcolor: completionPercentage === 100 ? 'success.main' : 'primary.main',
                                      height: 8,
                                      borderRadius: 1,
                                      width: `${completionPercentage}%`
                                    }}
                                  />
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Attempts breakdown */}
                    <Grid item xs={12}>
                      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Problem Solution Attempts
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Problem</TableCell>
                                  <TableCell align="center">Attempts</TableCell>
                                  <TableCell align="center">Result</TableCell>
                                  <TableCell align="center">Difficulty</TableCell>
                                  <TableCell align="right">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {problems.map(problem => {
                                  const problemSubmissions = submissions.filter(s => s.problemId === problem.id);
                                  const solved = isProblemSolved(problem.id);
                                  const attempts = problemSubmissions.length;
                                  return (
                                    <TableRow key={problem.id} hover>
                                      <TableCell>
                                        <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                          {createTextPreview(problem.problemStatement, 50)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">{attempts}</TableCell>
                                      <TableCell align="center">
                                        {solved ? (
                                          <Chip label="Solved" size="small" color="success" icon={<CheckIcon />} />
                                        ) : attempts > 0 ? (
                                          <Chip label="Attempted" size="small" color="warning" />
                                        ) : (
                                          <Chip label="Not Started" size="small" color="default" variant="outlined" />
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Chip 
                                          label={problem.difficultyLevel} 
                                          size="small"
                                          color={
                                            problem.difficultyLevel <= 2 ? 'success' :
                                            problem.difficultyLevel <= 4 ? 'primary' : 'error'
                                          }
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        <Button 
                                          size="small" 
                                          component={RouterLink}
                                          to={`/sessions/${sessionId}/problems/${problem.id}`}
                                          endIcon={<ArrowForwardIcon />}
                                          variant="text"
                                        >
                                          View
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Back button */}
                    <Grid item xs={12} sx={{ mt: 2, textAlign: 'center' }}>
                      <Button
                        variant="outlined"
                        component={RouterLink}
                        to={`/sessions/${sessionId}/problems`}
                        startIcon={<ArrowBackIcon />}
                      >
                        Back to Problems
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  // Regular session view
                  <>
                    <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <InfoIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">
                          Session Progress 
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Total Problems: {problems.length}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          Completed: {submissions.filter(s => s.isCorrect).length} / {problems.length}
                        </Typography>
                        <Typography variant="body2">
                          Accuracy: {
                            submissions.length > 0
                              ? Math.round((submissions.filter(s => s.isCorrect).length / submissions.length) * 100)
                              : 0
                          }%
                        </Typography>
                      </Box>
                      <Button 
                        variant="contained" 
                        size="small"
                        component={RouterLink}
                        to={`/sessions/${sessionId}/stats`}
                        endIcon={<ArrowForwardIcon />}
                      >
                        View Detailed Stats
                      </Button>
                    </Paper>
                  </>
                )}
                
                {!showStats && Object.keys(groups).map((groupIdStr) => {
                  const groupId = parseInt(groupIdStr);
                  const groupProblems = groups[groupId];
                  // First problem in group used for reference
                  // const firstProblem = groupProblems[0];
                  
                  // Calculate completion status for this group
                  const solvedInGroup = groupProblems.filter(p => isProblemSolved(p.id)).length;
                  const totalInGroup = groupProblems.length;
                  const completionPercentage = Math.round((solvedInGroup / totalInGroup) * 100);
                  
                  return (
                    <Accordion 
                        key={groupId}
                        defaultExpanded={groupId === initiallyExpandedGroupId}
                        sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                          bgcolor: (theme) => completionPercentage === 100 
                            ? theme.palette.success.light + '20'
                            : completionPercentage > 0 
                              ? theme.palette.primary.light + '20' 
                              : 'inherit',
                          '& .MuiAccordionSummary-content': {
                            flexDirection: 'column',
                            margin: '12px 0'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, width: '100%', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {getGroupTitle(groupId)}
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                            {(() => {
                              // The groupId here is a string from Object.keys, so we must convert to number
                              const groupIdNum = Number(groupId);
                              
                              // Find the group with matching ID
                              const foundGroup = problemGroups.find(g => g.id === groupIdNum);
                              
                              if (foundGroup?.hintsVideoUrl) {
                                return (
                                  <Chip
                                    label="Hint Video"
                                    color="secondary"
                                    size="small"
                                    component={RouterLink}
                                    to={`/hint-videos/${groupIdNum}`}
                                    clickable
                                    sx={{ mr: 1 }}
                                  />
                                );
                              }
                              return null;
                            })()}
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              {solvedInGroup} / {totalInGroup}
                            </Typography>
                            <Chip
                              label={completionPercentage === 100 ? 'Completed' : `${completionPercentage}%`}
                              color={completionPercentage === 100 ? 'success' : 'primary'}
                              size="small"
                              variant={completionPercentage === 100 ? 'filled' : 'outlined'}
                            />
                          </Box>
                        </Box>
                        
                        {/* Progress bar for problem group */}
                        <Box sx={{ width: '100%', mt: 1 }}>
                          <Box sx={{ 
                            width: '100%', 
                            bgcolor: 'grey.200', 
                            height: 8, 
                            borderRadius: 4,
                            position: 'relative',
                            overflow: 'hidden'
                          }}>
                            <Box sx={{ 
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              width: `${completionPercentage}%`,
                              bgcolor: completionPercentage === 100 
                                ? 'success.main' 
                                : 'primary.main',
                              borderRadius: 4,
                              transition: 'width 0.3s ease'
                            }} />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List sx={{ width: '100%', bgcolor: 'background.paper', pt: 0 }}>
                          {groupProblems.map((problem, index) => {
                            const result = getSubmissionResult(problem.id);
                            return (
                              <React.Fragment key={problem.id}>
                                {index > 0 && <Divider component="li" />}
                                <ListItem
                                  disablePadding
                                  secondaryAction={result.chip}
                                >
                                  <ListItemButton
                                    component={RouterLink}
                                    to={`/sessions/${sessionId}/problems/${problem.id}`}
                                    dense
                                  >
                                    <ListItemAvatar>
                                      <Avatar
                                        sx={{
                                          bgcolor: isProblemSolved(problem.id)
                                            ? 'success.light'
                                            : 'primary.light'
                                        }}
                                      >
                                        {isProblemSolved(problem.id) ? (
                                          <CheckIcon />
                                        ) : (
                                          <AssignmentIcon />
                                        )}
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={`Problem ${index + 1}: ${createTextPreview(problem.problemStatement, 60)}`}
                                      secondary={
                                        <Typography variant="body2" color={result.color}>
                                          {result.text}
                                        </Typography>
                                      }
                                    />
                                  </ListItemButton>
                                </ListItem>
                              </React.Fragment>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      </Container>
    </MainLayout>
  );
};

export default ProblemsPage;